import i18n from '@/i18n'
import CryptoJS from 'crypto-js'
import store, {
	getChat,
	getSortedUsersByIds,
	getChats,
	getRecentChats,
	browser,
} from '@/store'
import { createApp } from 'vue'
import router from '@/router'
import moment from 'moment'
import { getPusherInstance } from '@/helpers/pusher'
import {
	getRecentChatsToDisplay,
	isChatInPinned,
	isChatInRecents,
} from '@/store/chats'
import { storage, EventBusPlugin } from '@/helpers/utils'
import { environments } from '@/helpers/constants'
import { endSession } from '@/helpers/session'

export const COOKIE_KEY = 'pacct'

function isJson(item) {
	item = typeof item !== 'string' ? JSON.stringify(item) : item

	try {
		item = JSON.parse(item)
	} catch (e) {
		return false
	}

	if (typeof item === 'object' && item !== null) {
		return true
	}

	return false
}

export function copyToClipboard(text) {
	const clipboardData =
		event.clipboardData ||
		window.clipboardData ||
		event.originalEvent?.clipboardData ||
		navigator.clipboard

	clipboardData.writeText(text)
}

export function highlightFocus(element) {
	if (!element) return false
	const borderelm = document.querySelector('.accessibility-highlight-border')
	borderelm.style.display = 'none'
	const rect = element.getBoundingClientRect()
	const expandOffset = 5

	let top = rect.top
	let right = rect.right
	let bottom = rect.bottom
	let left = rect.left
	let width = rect.width
	let height = rect.height

	if (
		top !== 0 &&
		right !== window.innerWidth &&
		bottom !== window.innerHeight &&
		left !== 0
	) {
		top = top - expandOffset
		left = left - expandOffset
		width = width + expandOffset * 2
		height = height + expandOffset * 2
	} else {
		top = top + expandOffset
		right = right - expandOffset
		bottom = bottom - expandOffset
		left = left + expandOffset
		width = width - expandOffset * 2
		height = height - expandOffset * 2
	}

	const borderstyle = `
    display: block;
    top: ${top}px;
    right: ${right}px;
    bottom: ${bottom}px;
    left: ${left}px;
    width: ${width}px;
    height: ${height}px;
    border-radius: 6px;
    z-index: 10100;
  `
	borderelm.style = borderstyle
}

export function getSrcSet(imgPath) {
	const ext = imgPath.substr(imgPath.lastIndexOf('.'), imgPath.length)
	return ['2x', '3x']
		.map((x) => imgPath.replace(ext, `@${x}${ext} ${x}`))
		.join(', ')
}

export function generateUserKey() {
	const characters = '0123456789abcdef'
	let result = ''
	for (let i = 0; i < 40; i++) {
		const randomIndex = Math.floor(Math.random() * characters.length)
		result += characters[randomIndex]
	}
	return result
}

export function showGlobalSpinner(element_selector) {
	EventBus.$emit('showGlobalSpinner', {
		element: document.querySelector(element_selector),
	})
}

export function hideGlobalSpinner() {
	EventBus.$emit('hideGlobalSpinner')
}

export function encryptData(data, key) {
	key = key || 'abc' // protect against cypress tests that don't have a userKey

	if (Object.prototype.toString.call(data) === '[object Object]') {
		data = JSON.stringify(data)
	}

	var ciphertext = CryptoJS.AES.encrypt(data, key).toString()

	return ciphertext
}

export function decryptData(data, key) {
	//console.log({type: 'decryption', decrypted_data: data, decryption_key: key});
	var bytes = CryptoJS.AES.decrypt(data, key)

	var orig = bytes.toString(CryptoJS.enc.Utf8)

	if (isJson(orig)) {
		orig = JSON.parse(orig)
	}

	return orig
}

export function bulkDecrypt(items, key) {
	if (!items || !key) return false
	return items.map(({ encrypted }) => decryptData(encrypted, key))
}

export function cookieExists(cname) {
	var items = document.cookie.split(';')

	var names = items.map((item) => {
		return item.trim().split('=')[0]
	})

	return names.indexOf(cname) == -1 ? false : true
}

export function dateFormat(dt, format = 'll, LT') {
	if (!dt) return ''
	return dt.includes('Z') || dt.includes('+00:00')
		? moment(dt).format(format)
		: moment(dt + 'Z').format(format)
}

export function setCookie(cname, cvalue, domain) {
	console.debug('setting cookie...', cname, cvalue, domain)
	const d = new Date()
	d.setTime(d.getTime() + 183 * 24 * 60 * 60 * 1000)
	deleteCookie(cname, domain)
	const keys = {
		[cname]: cvalue,
		expires: d.toUTCString(),
		path: '/',
	}
	if (domain || location.host.includes('local.')) {
		keys.domain = domain || location.host.replace('local.', '')
	}
	document.cookie =
		Object.entries(keys)
			.map((pair) => pair.join('='))
			.join(';') + ';secure;'
}

export function deleteCookie(cname, domain = location.host) {
	console.debug('deleting cookie...', cname, domain)
	if (window.location.origin.includes('app.vite')) {
		domain = 'app.vite'
	}
	document.cookie = `${cname}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;secure;`
	document.cookie = `${cname}=;domain=.${domain};expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;secure;`
	document.cookie = `${cname}=;domain=${domain};expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;secure;`
	//document.cookie = `${cname}=;domain=.` + location.host.replace('local.', '') + `;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;`;
}

export function getCookie(cookieKey) {
	const allCookies = document.cookie.split('; ')
	const cookieStr = allCookies.find((cStr) => cStr.startsWith(cookieKey))
	if (!cookieStr) return null
	const cookie = cookieStr.split('=').slice(1).join('=')
	if (!cookie) return null
	try {
		return JSON.parse(cookie)
	} catch (e) {
		return cookie
	}
}

export function uuid() {
	return window.crypto.randomUUID()
}

export function mediaType(filetype) {
	let mediatype
	let type = filetype.split('/')[0]
	const subtype = filetype.split('/')[1]

	if (subtype == 'heic') {
		type = 'application'
	}
	switch (type) {
		case 'image':
			mediatype = 'PHOTO'
			break
		case 'video':
			mediatype = 'VIDEO'
			break
		case 'application':
			mediatype = 'FILE'
			break
		default:
			mediatype = 'FILE'
			break
	}

	return mediatype
}

export const getAccountCookieId = ({
	id,
	organization,
	organizations = [],
}) => {
	const org = organization || organizations[0] || {}
	let cookieId = `${COOKIE_KEY}_${org.id}_${id}`
	const branchName = getPreviewBranchName()
	if (branchName) {
		cookieId += `_${branchName}`
	}
	if (getEnv() !== environments.PROD) {
		cookieId += `_${getEnv()}`
	}
	return cookieId
}

export const getAccountCookie = (account) => {
	return getCookie(getAccountCookieId(account))
}

export const getEnv = () => {
	// beta uses prod api so has to have special check
	return window.appConfig.env || environments.PROD
}

export const getLinkForAccount = ({ id, organization, organizations = [] }) => {
	const org = organization || organizations[0] || {}
	// use org shortname or fallback subdomain
	const subdomain = org.shortname || `org-${org.id}`
	// now check if there is already an active account for this domain
	const accounts = store.getActiveUsers()
	const root = getRootDomain()
	const delimiter = root.startsWith('--') ? '' : '.'
	let hostname = `${subdomain}${delimiter}${root}`
	const hasConflict = accounts.some(
		(a) => a.hostname?.startsWith(hostname) && a.id !== id
	)
	if (hasConflict) {
		// there is already an account for this domain so we need to append
		// the user id so we don't have any conflicts
		hostname = `${subdomain}-user-${id}${delimiter}${root}`
	}
	// no conflicting domain so we can go ahead with this one
	return `https://${hostname}`
}

export const getDeepLinkDomain = () => {
	const root = getRootDomain()
	const delimiter = root.startsWith('--') ? '' : '.'
	return `https://app${delimiter}${root}`
}

export const getLoginUrl = () => {
	const root = getRootDomain()
	const delimiter = root.startsWith('--') ? '' : '.'
	return `https://accounts${delimiter}${root}`
}

export const setDesktopLoginUrls = async (accounts) => {
	if (!window.todesktop) return
	if (!accounts?.length) {
		accounts = store.getActiveUsers()
	}
	const domains = accounts
		.map((a) => a.origin || getLinkForAccount(a.user || a))
		.concat(getLoginUrl())
		.concat(getDeepLinkDomain())
	await addDesktopLoginUrls(domains)
}

export const addDesktopLoginUrls = async (urls) => {
	if (!window.todesktop || !urls) return
	const hostnames = urls.map((url) => new URL(url).hostname)
	const regex = await window.todesktop.app.getInternalUrls()
	const domains = (regex || '')
		.replace(/\.\*\(|\)\.\*/g, '')
		.split('|')
		.concat(hostnames)
	const piped = Array.from(new Set(domains)).join('|')
	window.todesktop.app.updateInternalUrls(`.*(${piped}).*`)
}

export const getRootDomain = () => {
	const { hostname } = window.location
	if (hostname.includes('--')) {
		// handle branch previews that are routed through CloudFront
		const domain = hostname.split('--')[1]
		return `--${domain}`
	}
	switch (getEnv()) {
		case 'local':
			return 'app.vite:5173'
		case 'dev':
			return 'dev.pronto.io'
		case 'beta':
			return 'beta.pronto.io'
		case 'sandbox':
			return 'sandbox.pronto.io'
		default:
			return 'pronto.io'
	}
}

export const getPreviewBranchName = () => {
	const { hostname } = window.location
	if (!hostname.includes('--')) return null
	return hostname.split('.')[0]?.split('--')?.[1] ?? null
}

export const serializeAccountCookie = (user, host) => {
	const org = user.organization || user.organizations[0]
	// serializing all of the pertinent info into a single string
	return [
		host || window.location.host,
		encodeURI(user.firstname),
		encodeURI(user.lastname),
		encodeURI(org.name),
		org.shortname,
		org.profilepic ? org.profilepicpath : null,
	].join('|')
}

export const deserializeAccountCookie = (id, cookie) => {
	const [cookieKey, orgId, userId] = id.split('_')
	const [
		hostname,
		firstname,
		lastname,
		name,
		shortname,
		profilepicpath,
	] = cookie.split('|')
	const origin = `https://${hostname}`
	const isPreview = ['app.vite', '--'].some((str) => hostname.includes(str))
	return {
		hostname,
		origin,
		id: parseInt(userId),
		cookie_id: id,
		firstname: decodeURI(firstname),
		lastname: decodeURI(lastname),
		isPreview,
		organization: {
			id: parseInt(orgId),
			name: decodeURI(name),
			shortname,
			profilepic: !!profilepicpath,
			profilepicurl: profilepicpath ? `${origin}${profilepicpath}` : null,
			profilepicpath,
		},
		raw: cookie,
	}
}

export const getCookieDomain = () => {
	if (window.location.origin.includes('app.vite')) {
		return 'app.vite'
	}
	return 'pronto.io'
}

export const createLoginTokenUrlForAccount = (
	user,
	logintoken,
	params = {}
) => {
	// get link and all necessary params
	const link = getLinkForAccount(user)
	const queries = Object.entries({ logintoken, ...params })
		.map((pair) => pair.join('='))
		.join('&')
	return `${link}?${queries}`
}

export const redirectToAccount = async (user, token, params = {}) => {
	const link = createLoginTokenUrlForAccount(user, token, params)
	if (window.Cypress) {
		if (window.CYPRESS_REDIRECT_SPY) {
			window.CYPRESS_REDIRECT_SPY(link)
		}
		return
	}
	await addDesktopLoginUrls([link])
	window.location = link
}

export const redirectToAccountInNewTab = async (user, token, params = {}) => {
	const link = createLoginTokenUrlForAccount(user, token, params)
	if (window.Cypress) {
		if (window.CYPRESS_REDIRECT_SPY) {
			window.CYPRESS_REDIRECT_SPY(link)
		}
		return
	}
	await addDesktopLoginUrls([link])
	return window.open(link)
}

export const getMentionIdsForMessage = (msg) => {
	try {
		const text = msg.message || msg.text
		if (!text || !text.includes('<@')) return []
		if (msg.mentions && msg.mentions.length) {
			return msg.mentions.map((m) => (m.id ? m.id : m)).filter((id) => id)
		}
		const matches = text.match(/(<@[0-9]+>)/g) || []
		return matches
			.map((match) => parseInt(match.replace(/[<>@]/g, '')))
			.filter((id) => id)
	} catch (error) {
		console.error(msg, error)
	}
}

export function renderVueComponent(component, props, rootElement) {
	const app = createApp(component, props)
	app.use(i18n)
	app.use(EventBusPlugin)
	const el = rootElement || document.createElement('div')
	const instance = app.mount(el)
	instance.$unmount = () => app.unmount()
	return instance
}

export const isElementInView = (element, container) => {
	if (!element || !container) return false
	const {
		top: containerTop,
		bottom: containerBottom,
	} = container.getBoundingClientRect()
	const { top: elemTop, bottom: elemBottom } = element.getBoundingClientRect()
	return elemBottom >= containerTop && elemTop <= containerBottom
}

export const asyncIsElementInView = (element, container) => {
	// this is async so that we avoid causing forced reflow issues
	return new Promise((resolve) => {
		if (!element || !container) return resolve(false)
		window.requestAnimationFrame(() => {
			resolve(isElementInView(element, container))
		})
	})
}

export const isElementAboveView = (element, container) => {
	if (!element || !container) return false
	const { top: containerTop } = container.getBoundingClientRect()
	const { bottom: elemBottom } = element.getBoundingClientRect()
	return elemBottom <= containerTop
}

export const asyncIsElementAboveView = (element, container) => {
	// this is async so that we avoid causing forced reflow issues
	return new Promise((resolve) => {
		if (!element || !container) return resolve(false)
		window.requestAnimationFrame(() => {
			return resolve(isElementAboveView(element, container))
		})
	})
}

export const hasScrolledPastElement = (element, container) => {
	if (!element || !container) return false
	const { bottom: containerBottom } = container.getBoundingClientRect()
	const { top: elemTop } = element.getBoundingClientRect()
	return containerBottom < elemTop
}

export const asyncHasScrolledPastElement = (element, container) => {
	// this is async so that we avoid causing forced reflow issues
	return new Promise((resolve) => {
		if (!element || !container) return resolve(false)
		window.requestAnimationFrame(() => {
			return resolve(hasScrolledPastElement(element, container))
		})
	})
}

export const getDeviceInfo = () => {
	const deviceType = storage.get('device_type') || 'WEB'

	return {
		browsername: browser.name,
		browserversion: browser.version,
		osname: browser.os,
		type: window.todesktop ? 'DESKTOP' : deviceType,
	}
}

export const buildSystemMessage = (message) => {
	if (!message) return null
	const { systemmessageparts: parts, systemevent } = message
	if (!parts) return message.message

	const { title, who_name, target_name, task_title, addedcount } = parts

	switch (systemevent) {
		case 'BUBBLE_CREATED':
			return i18n.global.t('model_systemevent_bubblecreated', [title])
		case 'BUBBLE_JOIN':
			return i18n.global.t('model_systemevent_inline_bubblejoin_invited', [
				who_name,
			])
		case 'BUBBLE_LEAVE':
			return i18n.global.t('model_systemevent_inline_bubbleleave')
		case 'BUBBLE_KICKED':
			return i18n.global.t('model_systemevent_inline_bubblekicked', [who_name])
		case 'TITLE_UPDATE':
			return i18n.global.t('model_systemevent_inline_titleupdate', [title])
		case 'DROPIN_INVITED':
		case 'DM_CREATED':
			return i18n.global.t('model_systemevent_inline_dmcreated', [target_name])
		case 'TASK_ASSIGNED':
			return i18n.global.t('model_systemevent_inline_taskassigned')
		case 'TASK_REASSIGNED':
			return i18n.global.t('model_systemevent_inline_taskreassigned', [
				task_title,
			])
		case 'TASK_CLAIMED':
			return i18n.global.t('model_systemevent_inline_taskclaimed', [task_title])
		case 'TASK_COMPLETED':
			return i18n.global.t('model_systemevent_inline_taskcompleted', [
				task_title,
			])
		case 'TASK_UNCOMPLETED':
			return i18n.global.t('model_systemevent_inline_taskuncompleted', [
				task_title,
			])
		case 'TASK_DELETED':
			return i18n.global.t('model_systemevent_taskdeleted', [
				who_name,
				task_title,
			])
		case 'TASK_DUEDATECHANGED':
			return i18n.global.t('model_systemevent_inline_taskduedatechanged')
		case 'BUBBLE_ADDED_MANY':
			return i18n.global.t('model_systemevent_inline_bubbleadded_many', [
				'',
				addedcount,
			])
		case 'VIDEOSESSION_STARTED':
			return i18n.global.t('model_systemevent_videosessionstarted')
		case 'VIDEOSESSION_STOPPED':
			return i18n.global.t('chat_meeting_was_hosted', [who_name])
		default:
			return null
	}
}

export const inMeeting = () => {
	const route = router.currentRoute.value
	return route.path.startsWith('/meeting')
}

export const getTypingIndicatorText = (userIds = []) => {
	const [a, b, c] = getSortedUsersByIds(userIds.slice(0, 3))
	switch (userIds.length) {
		case 0:
			return null
		case 1:
			return i18n.global.t('model_typists_one', [a.fullname])
		case 2:
			return i18n.global.t('model_typists_two', [a.fullname, b.fullname])
		case 3:
			return i18n.global.t('model_typists_three', [
				a.fullname,
				b.fullname,
				c.fullname,
			])
		default:
			return i18n.global.t('model_typists_many', [userIds.length])
	}
}

export const fakeWait = (ms = 1000, value) => {
	return new Promise((resolve) => {
		console.debug('Fake wait...', ms, 'ms')
		setTimeout(() => resolve(value), ms)
	})
}

export const waitIfKey = (key, ms = 1000) => {
	if (!key) return false
	const val = localStorage.getItem(key)
	if (!val) return true
	const int = parseInt(val, 10)
	return fakeWait(int || ms)
}

export const isGroupInRoute = (id) => {
	const route = router.currentRoute.value
	if (!id || !route) return false
	return route.name === 'chat' && route.params.id == id
}

export const redirectToRecentsOrChat = (id) => {
	const chat = getChat(id)
	if (isChatInPinned(chat)) return router.push(`/pinned/${id}`)
	if (isChatInRecents(chat)) return router.push(`/recents/${id}`)
	return router.push(`/chat/${id}`)
}

export const redirectToLastActiveGroup = () => {
	const visibleChats = getChats().filter((chat) => !chat.ishidden)
	const recentChats = getRecentChats()

	//if we don't have any bubbles that are not hidden we should direct to none
	if (!visibleChats.length) {
		return router.push('/group/none')
	}

	// if we have a cached lastActiveGroup and its not hidden, use that
	const lastActiveChat = getChat(localStorage.getItem('lastActiveGroupId'))
	if (lastActiveChat?.id && !lastActiveChat.ishidden) {
		return redirectToRecentsOrChat(lastActiveChat.id)
	}

	const path = recentChats.length
		? `/recents/${recentChats[0].id}`
		: `/chat/${visibleChats[0].id}`
	if (router.currentRoute.path === path) return
	router.push(path)
}

export const logoutUser = async () => {
	if (store.auth.api_token) {
		try {
			await post('/api/v1/user.logout')
		} catch (e) {}
	}
	const pusher = getPusherInstance()
	if (pusher) {
		pusher.allChannels().forEach((channel) => {
			pusher.unsubscribe(channel.name)
		})
	}

	endSession()
	deleteCookie('api_token')
	deleteCookie('media_token')
	store.removeActiveUserCookie()
	store.auth.remove()
	store.translations.reset()
	store.category.reset()
	store.courses.init()
	store.groups.init()
	store.dbDeleteDatabase()
	localStorage.removeItem('external_user_id')
	localStorage.removeItem('persist')
	localStorage.removeItem('device_uuid')
	localStorage.removeItem('device_type')
	localStorage.removeItem('acceptedtos')
	localStorage.removeItem('media_token')
	localStorage.removeItem('logging_enabled')
	localStorage.removeItem('pronto_paused_gifs')
	localStorage.removeItem('hideNotificationPrompt')

	if (window.gtag) {
		const { VITE_ANALYTICS_ID } = import.meta.env
		window.gtag('config', VITE_ANALYTICS_ID, {
			user_id: null,
		})
		window.gtag('set', VITE_ANALYTICS_ID, {
			org_id: null,
			org_name: null,
		})
	}
}

export const getErrorText = (error) => {
	switch (error) {
		case 'UNAUTHORIZED':
			return 'apierrors_UNAUTHORIZED'
		case 'MISSING_TITLE':
			return 'apierrors_MISSING_TITLE'
		case 'BUBBLE_NOTFOUND':
			return 'apierrors_BUBBLE_NOTFOUND'
		case 'BUBBLE_ACCESSDENIED':
			return 'apierrors_BUBBLE_ACCESSDENIED'
		case 'MISSING_EMAILS':
			return 'apierrors_MISSING_EMAILS'
		case 'MISSING_USERS':
			return 'apierrors_MISSING_USERS'
		case 'INVALID_STRING_TITLE':
			return 'apierrors_INVALID_STRING_TITLE'
		case 'INVALID_STRING_SOURCE':
			return 'apierrors_INVALID_STRING_SOURCE'
		case 'MISSING_MESSAGE':
			return 'apierrors_MISSING_MESSAGE'
		case 'MESSAGE_ACCESSDENIED':
			return 'apierrors_MESSAGE_ACCESSDENIED'
		case 'MESSAGE_NOTFOUND':
			return 'apierrors_MESSAGE_NOTFOUND'
		case 'MISSING_PHONE_COUNTRY':
			return 'apierrors_MISSING_PHONE_COUNTRY'
		case 'INVALID_PHONE_PHONE':
			return 'apierrors_INVALID_PHONE_PHONE'
		case 'INVALID_EMAIL_EMAIL':
			return 'apierrors_INVALID_EMAIL_EMAIL'
		case 'INVALID_LENGTH_CODE':
			return 'apierrors_INVALID_LENGTH_CODE'
		case 'INCORRECT_CODE':
			return 'apierrors_INCORRECT_CODE'
		case 'SEND_LIMIT_REACHED':
			return 'apierrors_SEND_LIMIT_REACHED'
		case 'VIDEOSESSION_FULL':
			return 'apierrors_VIDEOSESSION_FULL'
		case 'BUBBLE_MEMBERSHIPS_FULL':
			return 'apierrors_BUBBLE_MEMBERSHIPS_FULL'
		case 'ACCESSCODE_INVALID':
			return 'apierrors_ACCESSCODE_INVALID'
		case 'ACCESSCODE_EXPIRED':
			return 'apierrors_ACCESSCODE_EXPIRED'
		case 'ACCESSCODE_BANNED':
			return 'apierrors_ACCESSCODE_BANNED'
		case 'REDEMPTION_THROTTLED':
			return 'apierrors_REDEMPTION_THROTTLED'
		case 'PHONE_NOT_FOUND':
			return 'apierrors_PHONE_NOT_FOUND'
		case 'EMAIL_NOT_FOUND':
			return 'apierrors_EMAIL_NOT_FOUND'
		case 'DM_NOT_ALLOWED':
			return 'apierrors_DM_NOT_ALLOWED'
		case 'INVITE_NOT_ALLOWED':
			return 'apierrors_INVITE_NOT_ALLOWED'
		case 'TASK_NOT_FOUND':
			return 'apierrors_TASK_NOT_FOUND'
		case 'TASK_ALREADY_CLAIMED':
			return 'apierrors_TASK_ALREADY_CLAIMED'
		case 'ACCESS_DENIED':
			return 'apierrors_BUBBLE_ACCESSDENIED'
		case 'NO_PERMISSION':
			return 'apierrors_NO_PERMISSION'
		case 'LAST_OWNER_ERROR':
			return 'apierrors_LAST_OWNER_ERROR'
		default:
			return 'apierrors_error_generic'
	}
}

export const getErrorTextFromCode = (code) => {
	const text = getErrorText(code)
	if (!text || text === 'apierrors_error_generic') return null
	return text
}
